import {
  RedoOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import React, { useEffect, useRef, useState } from "react";

import api from "@/utils/api";
import http from "@/utils/http";

const { Text } = Typography;
const DATE_FORMAT = "MM/DD/YYYY";

const money = (value) => {
  const amount = Number(value || 0);
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
};

const statusColor = (record) => {
  if (record.type === 1) return "blue";
  if (record.type === 2) return "red";
  return record.printed ? "green" : "gold";
};

const VoidCheckHistoryPanel = ({ refreshKey }) => {
  const [form] = Form.useForm();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reissuingId, setReissuingId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [trustOptions, setTrustOptions] = useState([]);
  const [trustLoading, setTrustLoading] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const vendorTimerRef = useRef(null);
  const propertyTimerRef = useRef(null);
  const lastFiltersRef = useRef({});

  const loadHistory = async (
    filters = lastFiltersRef.current,
    page = pagination.current,
    count = pagination.pageSize,
  ) => {
    const params = {
      page,
      count,
      checkDate: filters.checkDate?.format("YYYY-MM-DD"),
      checkNo: filters.checkNo?.trim() || undefined,
      batch: filters.batch,
      vendorId: filters.vendorId,
      trustId: filters.trustId,
      propId: filters.propId,
    };
    setLoading(true);
    try {
      const response = await http.get(api.getVoidCheckHistory, { params });
      if (response?.code !== "1") {
        message.error(response?.msg || "Void check history could not be loaded.");
        return;
      }
      const pageData = response.data || {};
      setGroups(pageData.list || []);
      setPagination({
        current: pageData.pageNum || page,
        pageSize: pageData.pageSize || count,
        total: pageData.total || 0,
      });
    } catch (_error) {
      message.error("Void check history could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(lastFiltersRef.current, 1, pagination.pageSize);
    // refreshKey changes only after a successful Void or Void & Re-issue operation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(
    () => () => {
      clearTimeout(vendorTimerRef.current);
      clearTimeout(propertyTimerRef.current);
    },
    [],
  );

  const runSearch = (values) => {
    lastFiltersRef.current = values;
    loadHistory(values, 1, pagination.pageSize);
  };

  const resetSearch = () => {
    form.resetFields();
    lastFiltersRef.current = {};
    loadHistory({}, 1, pagination.pageSize);
  };

  const loadVendors = async (content = "") => {
    setVendorLoading(true);
    try {
      const response = await http.get(api.vendorSearch, { params: { content } });
      if (response?.code !== "1") return;
      setVendorOptions(
        (response.data || []).map((item) => ({
          value: item.vendid,
          label: [item.vendorId, item.vendorName].filter(Boolean).join(" - "),
        })),
      );
    } catch (_error) {
      message.warning("Vendor options could not be loaded.");
    } finally {
      setVendorLoading(false);
    }
  };

  const searchVendors = (content) => {
    clearTimeout(vendorTimerRef.current);
    vendorTimerRef.current = setTimeout(() => loadVendors(content), 300);
  };

  const loadTrusts = async () => {
    if (trustOptions.length) return;
    setTrustLoading(true);
    try {
      const response = await http.get(api.getTrustDictList);
      setTrustOptions(
        (response?.data || []).map((item) => ({
          value: item.trustId,
          label: item.trustNameTitle || item.trust,
        })),
      );
    } catch (_error) {
      message.warning("Trust options could not be loaded.");
    } finally {
      setTrustLoading(false);
    }
  };

  const loadProperties = async (content = "") => {
    setPropertyLoading(true);
    try {
      const response = await http.get(api.propSearch, { params: { content } });
      if (response?.code !== "1") return;
      setPropertyOptions(
        (response.data || []).map((item) => ({
          value: item.propId,
          label: item.propName || item.prop,
        })),
      );
    } catch (_error) {
      message.warning("Property options could not be loaded.");
    } finally {
      setPropertyLoading(false);
    }
  };

  const searchProperties = (content) => {
    clearTimeout(propertyTimerRef.current);
    propertyTimerRef.current = setTimeout(() => loadProperties(content), 300);
  };

  const reissue = (group) => {
    Modal.confirm({
      title: `Re-issue Void ID ${group.voidId}?`,
      content:
        "A copy of the original bill and its attachments will be created as a new pending check. The original and void records remain unchanged.",
      okText: "Re-issue",
      onOk: async () => {
        setReissuingId(group.voidId);
        try {
          // Standalone re-issue is intentionally linked by voidId, not transaction seq.
          const response = await http.get(api.getVoidedCheckReissue, {
            params: { voidId: group.voidId },
          });
          if (response?.code !== "1") {
            message.error(
              response?.msg || "The check could not be re-issued.",
            );
            return;
          }
          message.success(response?.msg || "The check was re-issued as pending.");
          await loadHistory();
        } catch (error) {
          message.error(
            error?.response?.data?.msg ||
              error?.message ||
              "The check could not be re-issued.",
          );
        } finally {
          setReissuingId(null);
        }
      },
    });
  };

  const columns = [
    {
      title: "Check Status",
      dataIndex: "status",
      width: 220,
      fixed: "left",
      render: (value, record) => <Tag color={statusColor(record)}>{value}</Tag>,
    },
    { title: "System Date", dataIndex: "systemDate", width: 155 },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 115,
      align: "right",
      render: (value) => <span className="void-check-money">{money(value)}</span>,
    },
    { title: "Check / Void Date", dataIndex: "date1", width: 135 },
    {
      title: "Check #",
      dataIndex: "checkNo",
      width: 105,
      render: (value) => <span className="void-check-code">{value || ""}</span>,
    },
    { title: "Property", dataIndex: "prop", width: 100 },
    { title: "GL Account", dataIndex: "glTitle", width: 210 },
    { title: "Vendor", dataIndex: "vendor", width: 190 },
    { title: "Module", dataIndex: "module", width: 140 },
    { title: "Bank", dataIndex: "bankName", width: 160 },
    { title: "Bank Account", dataIndex: "bankAccount", width: 145 },
    { title: "Trust", dataIndex: "trustName", width: 170 },
    { title: "System Bank #", dataIndex: "systemBankNumber", width: 125 },
    { title: "Batch #", dataIndex: "batch", width: 100 },
    { title: "By User", dataIndex: "byUser", width: 145 },
  ];

  return (
    <section className="void-check-workspace void-check-history-workspace">
      <Card
        className="void-check-filter-card"
        title={
          <div className="void-check-filter-title">
            <span>History filters</span>
            <Button type="text" icon={<ReloadOutlined />} onClick={resetSearch}>
              Reset
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={runSearch}>
          <Form.Item label="Original Check Date" name="checkDate">
            <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Check Number" name="checkNo">
            <Input allowClear placeholder="Search check number" />
          </Form.Item>
          <Form.Item label="Batch" name="batch">
            <InputNumber min={0} precision={0} stringMode controls={false} />
          </Form.Item>
          <Form.Item label="Vendor" name="vendorId">
            <Select
              allowClear
              showSearch
              filterOption={false}
              loading={vendorLoading}
              options={vendorOptions}
              placeholder="Search vendor"
              onSearch={searchVendors}
              onDropdownVisibleChange={(open) => open && loadVendors("")}
            />
          </Form.Item>
          <Form.Item label="Trust" name="trustId">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={trustLoading}
              options={trustOptions}
              placeholder="Select trust"
              onDropdownVisibleChange={(open) => open && loadTrusts()}
            />
          </Form.Item>
          <Form.Item label="Property" name="propId">
            <Select
              allowClear
              showSearch
              filterOption={false}
              loading={propertyLoading}
              options={propertyOptions}
              placeholder="Search property"
              onSearch={searchProperties}
              onDropdownVisibleChange={(open) => open && loadProperties("")}
            />
          </Form.Item>
          <Button
            block
            type="primary"
            htmlType="submit"
            icon={<SearchOutlined />}
            loading={loading}
          >
            Search History
          </Button>
        </Form>
      </Card>

      <Card
        className="void-check-result-card void-check-history-result"
        title="Voided check events"
        extra={
          <Space size={16}>
            <Text type="secondary">
              {pagination.total} event{pagination.total === 1 ? "" : "s"}
            </Text>
            <Button
              aria-label="Refresh history"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => loadHistory()}
            />
          </Space>
        }
      >
        <div className="void-check-history-list" aria-busy={loading}>
          {!loading && !groups.length ? (
            <Empty description="No void check history matches these filters." />
          ) : (
            groups.map((group) => (
              <article className="void-check-event" key={group.voidId}>
                <div className="void-check-event-header">
                  <div className="void-check-event-title-block">
                    <div className="void-check-event-title">
                      <span>Void ID {group.voidId}</span>
                      {group.canReissue ? (
                        <Tag color="gold">Ready to re-issue</Tag>
                      ) : (
                        <Tag color="green">Re-issued</Tag>
                      )}
                    </div>
                    <div className="void-check-event-meta">
                      <span>Void date {group.voidDate || "-"}</span>
                      <span>Void batch {group.voidBatch ?? "-"}</span>
                      <span>900 cutoff {group.postTo900Before || "-"}</span>
                      <span>Created {group.createTime || "-"}</span>
                    </div>
                    {group.remark && (
                      <div className="void-check-event-remark">{group.remark}</div>
                    )}
                  </div>
                  {group.canReissue && (
                    <Button
                      type="primary"
                      icon={<RedoOutlined />}
                      loading={reissuingId === group.voidId}
                      onClick={() => reissue(group)}
                    >
                      Re-issue
                    </Button>
                  )}
                </div>
                <Table
                  className="void-check-history-table"
                  rowKey="id"
                  size="small"
                  columns={columns}
                  dataSource={group.itemList || []}
                  pagination={false}
                  scroll={{ x: 2115 }}
                  rowClassName={(record) => `void-check-history-type-${record.type}`}
                />
              </article>
            ))
          )}
        </div>

        <Pagination
          className="void-check-history-pagination"
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          showQuickJumper
          pageSizeOptions={["10", "20", "50", "100"]}
          showTotal={(total) => `Total ${total} events`}
          onChange={(page, pageSize) =>
            loadHistory(lastFiltersRef.current, page, pageSize)
          }
        />
      </Card>
    </section>
  );
};

export default VoidCheckHistoryPanel;
